import {type FilterConfiguration} from "~/components/ottopilotDataViewer/filterConfiguration";
import {FilterOperation} from "@vesta/vos-types/modules/workspaceLevel/domain/FilterOperation";
import {OttopilotEventStatus, type OttopilotEvent} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEvent";
import type {OttopilotEventTemplate} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventTemplate";
import {OttopilotEventContainerStatus} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventContainersWorkspaceCapability/domain/OttopilotEventContainer";
import type {EventContainerTemplate} from "~/domainModels/workspaceModules/eventContainersModule";
import {
    EmotoradEventTypes,
    EmotoradLeadEventStatusEnum,
    EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID,
    emotoradLeadEventStatuses,
} from "~/domainModels/workspaceSpecific/emotorad";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, errorResult, type Result} from "@vesta/error-handling-remix/result";
import {generateUuid} from "@vesta/global-common-typescript/utilities/utilities";
import {isAmaronWorkspace} from "~/domainModels/workspaceSpecific/amaron";
import {amaronRestoreEventAction, amaronRestoreEventContainerAction} from "~/utilities/apis/amaronActionsApi";
import {isDuluxWorkspace} from "~/domainModels/workspaceSpecific/dulux";
import {duluxRestoreEventAction, duluxRestoreEventContainerAction} from "~/utilities/apis/duluxActionsApi";
import {
    getEventsWithEventContainers,
    updateEvent,
    type EventWithEventContainer,
    type EventContainerWithEvents,
} from "~/utilities/apis/api";
import {updateEventContainer} from "~/utilities/apis/eventContainersApi";
import type {WorkspaceCapability} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapability";
import type {WorkspaceCapabilityType} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/workspaceCapabilities/domain/WorkspaceCapabilityType";

function resolveFirstNonArchiveKanbanStatusValue(
    eventTemplate: OttopilotEventTemplate,
    archiveStatusValue: Uuid | string,
): string | undefined {
    const archiveStatusId = String(archiveStatusValue);
    for (const column of eventTemplate.configuration.eventPage.kanbanBoardPageView.columnConfig) {
        for (const possibleValue of column.possibleValues) {
            if (String(possibleValue) !== archiveStatusId) {
                return String(possibleValue);
            }
        }
    }
    return undefined;
}

export function buildEventContainerRestoreUpdatePayload(
    statusColumnConfig: NonNullable<
        EventContainerTemplate["configuration"]["statusColumnConfig"]
    >,
): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        status: OttopilotEventContainerStatus.active,
    };
    if (statusColumnConfig.defaultStatusValue !== undefined) {
        payload[statusColumnConfig.columnId] = statusColumnConfig.defaultStatusValue;
    }
    return payload;
}

export function buildEventRestoreUpdatePayload(eventTemplate: OttopilotEventTemplate | undefined): Record<string, unknown> {
    const payload: Record<string, unknown> = {
        status: OttopilotEventStatus.active,
    };

    if (eventTemplate === undefined) {
        return payload;
    }

    if (eventTemplate.id === (EmotoradEventTypes.lead as Uuid)) {
        payload[EMOTORAD__EVENT__ENQUIRY__LEAD_STATUS__COLUMN_ID] =
            emotoradLeadEventStatuses[EmotoradLeadEventStatusEnum.OPEN].id;
    }

    const statusColumnConfig = eventTemplate.configuration.statusColumnConfig;
    if (statusColumnConfig !== undefined) {
        const activeStatusValue = resolveFirstNonArchiveKanbanStatusValue(
            eventTemplate,
            statusColumnConfig.archiveStatusValue,
        );
        if (activeStatusValue !== undefined) {
            payload[String(statusColumnConfig.columnId)] = activeStatusValue;
        }
    }

    return payload;
}

async function fetchEventContainerEvents(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    eventContainerId: Uuid;
}): Promise<Result<Array<Pick<OttopilotEvent, "id" | "templateId" | "status">>>> {
    const eventsResult = await getEventsWithEventContainers({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        workspaceId: args.workspaceId,
        filterConfiguration: {
            revisionId: generateUuid(),
            lineItems: [
                {
                    columnId: "eventContainerId",
                    operation: FilterOperation.equals,
                    value: args.eventContainerId,
                },
            ],
        } satisfies FilterConfiguration,
        sortConfiguration: {
            revisionId: generateUuid(),
            lineItems: [],
        },
        limit: 500,
        offset: 0,
    });

    if (eventsResult.success !== true) {
        return eventsResult;
    }

    return dataResult(
        eventsResult.data.map((event) => ({
            id: event.id,
            templateId: event.templateId,
            status: event.status,
        })),
    );
}

export async function restoreEventContainerWithEvents(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspace?: {displayName: string};
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    eventContainer: EventContainerWithEvents;
    statusColumnConfig: NonNullable<
        EventContainerTemplate["configuration"]["statusColumnConfig"]
    >;
    eventTemplates: ReadonlyArray<OttopilotEventTemplate>;
}): Promise<Result<void>> {
    if (args.workspaceCapabilities !== undefined && isAmaronWorkspace(args.workspaceCapabilities) === true) {
        const restoreResult = await amaronRestoreEventContainerAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventContainerId: args.eventContainer.id,
            },
        });
        if (restoreResult.success !== true) {
            return errorResult(restoreResult.error);
        }
        return dataResult(undefined);
    }
    if (args.workspaceCapabilities !== undefined && isDuluxWorkspace(args.workspaceCapabilities) === true) {
        const restoreResult = await duluxRestoreEventContainerAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventContainerId: args.eventContainer.id,
            },
        });
        if (restoreResult.success !== true) {
            return errorResult(restoreResult.error);
        }
        return dataResult(undefined);
    }

    const eventContainerUpdateResult = await updateEventContainer({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        id: args.eventContainer.id,
        templateId: args.eventContainer.templateId,
        workspaceId: args.workspaceId,
        data: buildEventContainerRestoreUpdatePayload(
            args.statusColumnConfig,
        ),
    });

    if (eventContainerUpdateResult.success !== true) {
        return eventContainerUpdateResult;
    }

    const eventsFromJourney =
        args.eventContainer.events.length > 0
            ? args.eventContainer.events.map((event) => ({
                  id: event.id,
                  templateId: event.templateId,
                  status: event.status,
              }))
            : null;

    const eventsResult =
        eventsFromJourney !== null
            ? dataResult(eventsFromJourney)
            : await fetchEventContainerEvents({
                  accessToken: args.accessToken,
                  intellsysBaseUrl: args.intellsysBaseUrl,
                  workspaceId: args.workspaceId,
                  eventContainerId: args.eventContainer.id,
              });

    if (eventsResult.success !== true) {
        return eventsResult;
    }

    for (const event of eventsResult.data) {
        if (event.status !== OttopilotEventStatus.archived) {
            continue;
        }

        const eventTemplate = args.eventTemplates.find((template) => template.id === event.templateId);
        const eventUpdateResult = await updateEvent({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            eventId: event.id,
            templateId: event.templateId,
            workspaceId: args.workspaceId,
            data: buildEventRestoreUpdatePayload(eventTemplate) as never,
        });

        if (eventUpdateResult.success !== true) {
            return errorResult(
                new Error(
                    eventUpdateResult.error instanceof Error
                        ? eventUpdateResult.error.message
                        : "Could not restore one or more events on this journey.",
                ),
            );
        }
    }

    return dataResult(undefined);
}

export async function restoreArchivedEvent(args: {
    accessToken: string;
    intellsysBaseUrl: string;
    workspaceId: Uuid;
    workspace?: {displayName: string};
    workspaceCapabilities?: ReadonlyArray<WorkspaceCapability<WorkspaceCapabilityType>>;
    event: EventWithEventContainer;
    eventTemplate: OttopilotEventTemplate | undefined;
    eventContainerTemplate: EventContainerTemplate;
}): Promise<Result<void>> {
    if (args.workspaceCapabilities !== undefined && isAmaronWorkspace(args.workspaceCapabilities) === true) {
        const restoreResult = await amaronRestoreEventAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventId: args.event.id,
                templateId: args.event.templateId,
            },
        });
        if (restoreResult.success !== true) {
            return errorResult(restoreResult.error);
        }
        return dataResult(undefined);
    }
    if (args.workspaceCapabilities !== undefined && isDuluxWorkspace(args.workspaceCapabilities) === true) {
        const restoreResult = await duluxRestoreEventAction({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            request: {
                workspaceId: args.workspaceId,
                eventId: args.event.id,
                templateId: args.event.templateId,
            },
        });
        if (restoreResult.success !== true) {
            return errorResult(restoreResult.error);
        }
        return dataResult(undefined);
    }

    const eventUpdateResult = await updateEvent({
        accessToken: args.accessToken,
        intellsysBaseUrl: args.intellsysBaseUrl,
        eventId: args.event.id,
        templateId: args.event.templateId,
        workspaceId: args.workspaceId,
        data: buildEventRestoreUpdatePayload(args.eventTemplate) as never,
    });

    if (eventUpdateResult.success !== true) {
        return eventUpdateResult;
    }

    const statusColumnConfig =
        args.eventContainerTemplate.configuration.statusColumnConfig;
    if (
        statusColumnConfig !== undefined &&
        args.event.eventContainer.status ===
            OttopilotEventContainerStatus.archived
    ) {
        const eventContainerUpdateResult = await updateEventContainer({
            accessToken: args.accessToken,
            intellsysBaseUrl: args.intellsysBaseUrl,
            id: args.event.eventContainer.id,
            templateId: args.event.eventContainer.templateId,
            workspaceId: args.workspaceId,
            data: buildEventContainerRestoreUpdatePayload(
                statusColumnConfig,
            ),
        });

        if (eventContainerUpdateResult.success !== true) {
            return eventContainerUpdateResult;
        }
    }

    return dataResult(undefined);
}
